// Setting up a sales replay: which source, which past days (default: the last 12 weeks),
// and how much extra the simulated production makes. Creating it starts the first run.

import { REPLAY_MAX_RANGE_DAYS, type ReplayScenarioOptions } from '@enp/domain/api';
import { newId } from '@enp/domain/ids';
import { addDays, businessToday, daysBetween, isPlainDate } from '@enp/domain/time';
import {
  Button,
  Card,
  CardContent,
  DateField,
  EmptyState,
  FormField,
  Input,
  LoadingSkeleton,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CircleAlert, FileInput, Lock, Play } from 'lucide-react';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { meQueryOptions } from '../../../auth/session.js';
import { ApiError } from '../../../lib/api.js';
import { ReplayBreadcrumb, useCanRunReplays } from '../../../replay/components.js';
import { defaultRange, rangeLength } from '../../../replay/labels.js';
import {
  connectionsQuery,
  connectorsQuery,
  createScenario,
  replayKeys,
  runningRunOf,
  type ScenarioCreateBody,
  scenariosQuery,
  startRun,
} from '../../../replay/queries.js';
import { PracticeBranchNote } from '../../../replay/report.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { useDocumentTitle } from '../../../shell/page.js';

export const Route = createFileRoute('/_app/replay/new')({
  validateSearch: z.object({
    /** A scenario to start from (same source, dates and options). */
    copy: z.uuid().optional().catch(undefined),
  }),
  component: NewReplayPage,
});

interface Values {
  connection: string;
  name: string;
  from: string;
  to: string;
  buffer: string;
}

/**
 * Field errors, plus `range`: a problem with the two days together (both fields are then
 * marked invalid and the message says it once, review UI2A-217).
 */
type Errors = Partial<Record<keyof Values | 'range', string>>;

function validate(v: Values, today: string): Errors {
  const e: Errors = {};
  if (!v.connection) e.connection = 'Pick the source to read sales from.';
  if (!isPlainDate(v.from)) e.from = 'Enter the first day to replay.';
  if (!isPlainDate(v.to)) e.to = 'Enter the last day to replay.';
  if (!e.from && !e.to) {
    if (v.from > v.to) e.range = 'The first day must be on or before the last day.';
    else if (v.to >= today)
      e.range = 'Only finished days can be replayed: pick yesterday or earlier.';
    else if (daysBetween(v.from, v.to) >= REPLAY_MAX_RANGE_DAYS)
      e.range = `A replay covers at most ${REPLAY_MAX_RANGE_DAYS} days.`;
  }
  const buffer = Number(v.buffer);
  if (v.buffer.trim() === '' || !Number.isFinite(buffer) || buffer < 0 || buffer > 500)
    e.buffer = 'Enter a percentage from 0 to 500.';
  return e;
}

function NewReplayPage() {
  useDocumentTitle('New sales replay');
  const { copy } = Route.useSearch();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  // Replays cover trading days: "today" is the current trading day (from the cut-off), so
  // a day still trading after local midnight is not offered as finished.
  const today = businessToday(me.account.timezone, { cutoff: me.account.trading_day_cutoff });
  const canRun = useCanRunReplays();
  const connections = useQuery(connectionsQuery());
  const connectors = useQuery(connectorsQuery());
  const scenarios = useQuery({ ...scenariosQuery(), enabled: Boolean(copy) });

  const salesKinds = new Set(
    (connectors.data?.connectors ?? [])
      .filter((c) => c.capabilities.includes('sales_source'))
      .map((c) => c.kind),
  );
  const sources = (connections.data?.connections ?? []).filter(
    (c) => salesKinds.has(c.connector_kind) && c.target_confirmed_at !== null,
  );

  if (connections.isPending || connectors.isPending || (copy && scenarios.isPending)) {
    return (
      <>
        <PageHeader eyebrow={<ReplayBreadcrumb page="New replay" />} title="New sales replay" />
        <LoadingSkeleton variant="form" count={4} label="Loading sources" />
      </>
    );
  }
  if (connections.isError || connectors.isError) {
    return (
      <>
        <PageHeader eyebrow={<ReplayBreadcrumb page="New replay" />} title="New sales replay" />
        <ErrorPanel
          error={connections.error ?? connectors.error}
          titleAs="h2"
          what="The sources"
          onRetry={() => {
            void connections.refetch();
            void connectors.refetch();
          }}
        />
      </>
    );
  }
  const template = copy ? scenarios.data?.items.find((s) => s.id === copy) : undefined;
  return (
    <>
      <PageHeader
        eyebrow={<ReplayBreadcrumb page="New replay" />}
        title="New sales replay"
        description="Pick the source and the past days to replay. The replay starts as soon as it’s set up and takes about a minute for 12 weeks."
      />
      <PracticeBranchNote scenario={undefined} />
      {!canRun ? (
        <EmptyState
          compact
          icon={Lock}
          title="Only catalogue editors can start a replay"
          description="It reads your till’s sales and writes a practice branch. Ask the owner, or open an existing report."
          action={
            <Button variant="secondary" asChild>
              <Link to="/replay">Back to Sales replay</Link>
            </Button>
          }
        />
      ) : sources.length === 0 ? (
        <EmptyState
          compact
          icon={FileInput}
          title="No source can deliver sales yet"
          description="Connect your till system under Imports and confirm its account. The replay reads past sales from that same connection."
          action={
            <Button asChild>
              <Link to="/imports">Go to Imports</Link>
            </Button>
          }
        />
      ) : (
        <ReplayForm
          today={today}
          sources={sources.map((s) => ({ id: s.id, name: s.display_name }))}
          initial={{
            connection:
              template?.connection_id ?? (sources.length === 1 ? (sources[0]?.id ?? '') : ''),
            name: '',
            ...(template ? { from: template.from, to: template.to } : defaultRange(today)),
            buffer: String(template?.options.production_buffer_pct ?? 10),
          }}
          copiedFrom={template?.name}
          templateOptions={template?.options}
        />
      )}
    </>
  );
}

function ReplayForm({
  today,
  sources,
  initial,
  copiedFrom,
  templateOptions,
}: {
  today: string;
  sources: { id: string; name: string }[];
  initial: Values;
  copiedFrom: string | undefined;
  /** The copied scenario's options, kept whole (only the buffer is on the form). */
  templateOptions: ReplayScenarioOptions | undefined;
}) {
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // One key per create attempt: a retried request never makes a second scenario. Any
  // change to the form is a new attempt.
  const attempt = useRef(newId());
  const set = (patch: Partial<Values>) => {
    attempt.current = newId();
    setValues((v) => ({ ...v, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(patch) as (keyof Values)[]) delete next[k];
      // Either day changing may settle a problem with the two together.
      if ('from' in patch || 'to' in patch) delete next.range;
      return next;
    });
  };

  const create = useMutation({
    mutationFn: async (body: ScenarioCreateBody) => {
      // A repeat of a create that got through answers the same scenario (its key); if its
      // run started too, the start is refused (409 `run_in_progress`): open that run.
      const scenario = await createScenario(body, attempt.current);
      try {
        return (await startRun(scenario.id)).id;
      } catch (error) {
        if (error instanceof ApiError && error.status === 409 && error.code === 'run_in_progress') {
          const running = await runningRunOf(queryClient, scenario.id);
          if (running) return running;
        }
        throw error;
      }
    },
    onSuccess: async (runId) => {
      await queryClient.invalidateQueries({ queryKey: replayKeys.scenarios() });
      toast.success('Replay started', { description: 'You can leave this page; it keeps going.' });
      await navigate({ to: '/replay/runs/$runId', params: { runId } });
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: replayKeys.scenarios() });
      const code = error instanceof ApiError ? error.code : undefined;
      if (code === 'idempotency_key_reused' || code === 'invalid_idempotency_key') {
        // The key no longer fits this form: the next press sends a fresh one.
        attempt.current = newId();
        setFailure(
          code === 'idempotency_key_reused'
            ? 'This form was already sent once with different details, so it wasn’t sent again. Check the details and press Start the replay once more.'
            : 'The request couldn’t be matched to this form, so nothing was set up. Press Start the replay once more.',
        );
        return;
      }
      setFailure(
        error instanceof ApiError
          ? error.message
          : 'The server couldn’t be reached. Check your connection and try again.',
      );
    },
  });

  const span = useMemo(
    () =>
      isPlainDate(values.from) && isPlainDate(values.to) && values.from <= values.to
        ? rangeLength(values.from, values.to)
        : null,
    [values.from, values.to],
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFailure(null);
    const found = validate(values, today);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const key = Object.keys(found)[0];
      const first = key === 'range' ? 'from' : key;
      document
        .querySelector<HTMLElement>(`[data-field="${first}"] input, [data-field="${first}"] button`)
        ?.focus();
      return;
    }
    const buffer = Number(values.buffer);
    create.mutate({
      connection_id: values.connection,
      name: values.name.trim() || undefined,
      from: values.from,
      to: values.to,
      // Only what the form changed: a copy keeps its template's options whole; a new
      // scenario leaves the rest to the server's defaults (review CR2-27).
      options: templateOptions
        ? { ...templateOptions, production_buffer_pct: buffer }
        : values.buffer.trim() !== initial.buffer
          ? { production_buffer_pct: buffer }
          : undefined,
    });
  };

  return (
    <Card className="max-w-2xl">
      <CardContent className="pt-6">
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-5"
          aria-label="New sales replay"
        >
          {copiedFrom && (
            <p className="text-sm text-muted">
              Starting from “{copiedFrom}”: same source, days and settings. It gets its own practice
              branch.
            </p>
          )}
          <div data-field="connection">
            <FormField label="Source" required error={errors.connection}>
              {(control) => (
                <Select value={values.connection} onValueChange={(v) => set({ connection: v })}>
                  <SelectTrigger {...control} className="w-full">
                    <SelectValue placeholder="Pick a source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sources.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-label font-medium text-fg">Days to replay</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div data-field="from">
                <FormField label="First day" required error={errors.from}>
                  {(control) => (
                    <DateField
                      {...control}
                      // A problem with the two days together marks both (UI2A-217).
                      aria-invalid={control['aria-invalid'] ?? (errors.range ? true : undefined)}
                      aria-describedby={
                        [control['aria-describedby'], errors.range && 'dates-help dates-error']
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      value={values.from || undefined}
                      today={today}
                      max={isPlainDate(values.to) ? values.to : addDays(today, -1)}
                      onChange={(v) => set({ from: v ?? '' })}
                    />
                  )}
                </FormField>
              </div>
              <div data-field="to">
                <FormField label="Last day" required error={errors.to}>
                  {(control) => (
                    <DateField
                      {...control}
                      aria-invalid={control['aria-invalid'] ?? (errors.range ? true : undefined)}
                      aria-describedby={
                        [control['aria-describedby'], errors.range && 'dates-help dates-error']
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      value={values.to || undefined}
                      today={today}
                      min={isPlainDate(values.from) ? values.from : undefined}
                      max={addDays(today, -1)}
                      onChange={(v) => set({ to: v ?? '' })}
                    />
                  )}
                </FormField>
              </div>
            </div>
            {/* Help, then the error: the same order as every field (UI2A-217). */}
            <p id="dates-help" className="text-sm text-muted" aria-live="polite">
              {span ? `${span} of trading.` : 'Trading days, both included.'} Suggested: the last 12
              weeks, so every weekday is seen a dozen times.
            </p>
            {errors.range && (
              <p
                id="dates-error"
                role="alert"
                className="flex items-start gap-1.5 text-sm font-medium text-state-danger comfortable:text-label"
              >
                <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
                <span>{errors.range}</span>
              </p>
            )}
          </fieldset>
          <div data-field="buffer">
            <FormField
              label="Production buffer (%)"
              required
              error={errors.buffer}
              description="Simulated production makes each day’s sales plus this much extra, like a real bake plan."
            >
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={500}
                step={1}
                value={values.buffer}
                onChange={(e) => set({ buffer: e.target.value })}
                className="w-32"
              />
            </FormField>
          </div>
          <div data-field="name">
            <FormField label="Name" description="Optional. Default: the days it covers.">
              <Input
                value={values.name}
                maxLength={120}
                placeholder="e.g. Winter 12 weeks"
                onChange={(e) => set({ name: e.target.value })}
              />
            </FormField>
          </div>
          {failure && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md bg-state-danger-subtle px-4 py-3 text-body text-fg"
            >
              <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-state-danger" />
              <span>
                <strong className="font-semibold">The replay didn’t start.</strong> {failure}
              </span>
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-5">
            <Button variant="secondary" asChild>
              <Link to="/replay">Cancel</Link>
            </Button>
            <Button type="submit" icon={<Play aria-hidden />} loading={create.isPending}>
              {create.isPending ? 'Starting…' : 'Start the replay'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
